import type { Student } from '@/lib/types';
import { gradeBadgeClass } from '@/lib/constants';

type Props = {
  gradeKey: string;
  label: string;
  students: Student[];
  expanded: boolean;
  onToggle: () => void;
  onEdit: (student: Student) => void;
  onDelete: (student: Student) => void;
};

function formatDate(value: string | null): string {
  if (!value) return '-';
  return value;
}

export default function StudentGradeGroup({
  gradeKey,
  label,
  students,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}: Props) {
  const isUngraded = gradeKey === '__none__';

  return (
    <div className="overflow-hidden rounded-lg border border-background-200 bg-white transition-colors">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full cursor-pointer items-center justify-between gap-3 px-4 py-3.5 text-left transition-colors hover:bg-background-50"
      >
        <div className="flex items-center gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${
              isUngraded ? 'bg-background-100 text-foreground-500' : gradeBadgeClass(gradeKey)
            }`}
          >
            {isUngraded ? <i className="ri-question-line text-base"></i> : label}
          </span>
          <div>
            <p className="text-sm font-bold text-foreground-950">{label}</p>
            <p className="mt-0.5 text-xs text-foreground-500">
              {expanded ? '펼쳐진 상태' : '눌러서 학생 보기'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-semibold text-secondary-900">
            총 {students.length}명
          </span>
          <span className="flex h-7 w-7 items-center justify-center text-foreground-400">
            <i className={`text-lg ${expanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
          </span>
        </div>
      </button>

      {expanded && (
        <div className="overflow-x-auto border-t border-background-100">
          <table className="w-full min-w-[880px] text-left text-sm">
            <thead className="bg-background-50">
              <tr className="border-b border-background-200">
                <th className="px-4 py-2.5 font-semibold text-foreground-600">이름</th>
                <th className="px-4 py-2.5 font-semibold text-foreground-600">생년월일</th>
                <th className="px-4 py-2.5 font-semibold text-foreground-600">학교</th>
                <th className="px-4 py-2.5 font-semibold text-foreground-600">보호자</th>
                <th className="px-4 py-2.5 font-semibold text-foreground-600">보호자 연락처</th>
                <th className="px-4 py-2.5 font-semibold text-foreground-600">학생 연락처</th>
                <th className="px-4 py-2.5 text-right font-semibold text-foreground-600">관리</th>
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr
                  key={s.id}
                  className="border-b border-background-100 transition-colors last:border-0 hover:bg-background-50"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${gradeBadgeClass(
                          s.grade
                        )}`}
                      >
                        {s.name.charAt(0)}
                      </span>
                      <span className="font-semibold text-foreground-900">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-600">
                    {s.birth_date ? (
                      formatDate(s.birth_date)
                    ) : (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-800">
                        <i className="ri-error-warning-line"></i>
                        생년월일 없음
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-foreground-600">{s.school || '-'}</td>
                  <td className="px-4 py-3 text-foreground-600">{s.parent_name || '-'}</td>
                  <td className="px-4 py-3 text-foreground-600">{s.parent_phone || '-'}</td>
                  <td className="px-4 py-3 text-foreground-600">{s.phone || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => onEdit(s)}
                        title="수정"
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-primary-600"
                      >
                        <i className="ri-pencil-line"></i>
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        title="삭제"
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-accent-100 hover:text-accent-700"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}